import React, { FC } from 'react';
import { useTheme } from 'react-native-paper';
import { View, ViewProps } from 'react-native';
import { ChargeRate, Currency, Maybe } from '../../types/appsync-types';
import Typography from '../Typography';
import Container, { ContainerProps } from '../Container';
import { useI18n } from '../../context/I18nContext';

type Props = {
  chargeRates: Maybe<Maybe<ChargeRate>[]> | undefined;
  currency: Currency;
  isLoading?: boolean;
  marginSize?: ContainerProps['marginSize'];
  style?: ViewProps['style'];
};

const units: [number, string][] = [
  [1, 'minute'],
  [60, 'hour'],
  [60 * 24, 'day'],
  [60 * 24 * 7, 'week'],
  [60 * 24 * 30, 'month'],
];

function getBestTimeUnit(minutes: number) {
  let bestUnit = units[0];
  units.forEach((unit) => {
    if (minutes >= unit[0]) {
      bestUnit = unit;
    }
  });
  return bestUnit;
}

export const SpacePricing: FC<Props> = ({ chargeRates, currency, isLoading = false, marginSize, style }) => {
  const { colors } = useTheme();
  const { formatCurrency, I18n, formatDuration } = useI18n();
  return (
    <Container marginSize={marginSize} style={style}>
      <Typography variant="h3" style={{ marginVertical: 4 }}>
        {I18n.t('mapview.spaceInfo.pricing')}
      </Typography>
      <View
        style={{
          flexDirection: 'row',
          paddingVertical: 12,
          paddingHorizontal: 4,
          borderColor: colors.primary,
          borderWidth: 1,
        }}
      >
        <Typography variant="body1" style={{ flex: 1, fontWeight: 'bold' }}>
          {I18n.t('mapview.spaceInfo.duration')}
        </Typography>
        <Typography variant="body1" style={{ flex: 1, fontWeight: 'bold' }}>
          {I18n.t('mapview.spaceInfo.price')}
        </Typography>
      </View>
      {chargeRates
        ? chargeRates.map((chargeRate, index) => {
            if (!chargeRate) return null;
            // TODO: Handle possible undefined values
            const [intervalDivisor, intervalUnit] = getBestTimeUnit(chargeRate.interval!);

            let minimumTimeString: string;
            if (chargeRate.min === 0) {
              minimumTimeString = !chargeRate.max ? '*' : '0';
            } else {
              minimumTimeString = formatDuration(chargeRate.min!, 'minutes', undefined, true);
            }

            const maximumTimeString: string = chargeRate.max
              ? formatDuration(chargeRate.max, 'minutes', undefined, true)
              : '';

            let durationString = '';
            if (maximumTimeString) {
              durationString = `${minimumTimeString} - ${maximumTimeString}`;
            } else {
              durationString =
                chargeRate.min === 0
                  ? minimumTimeString
                  : `${minimumTimeString}/ ${I18n.t('mapview.spaceInfo.longer')}`;
            }

            const currencyString = formatCurrency(chargeRate.price! / 100, currency);
            const intervalUnitAmount = chargeRate.interval! / intervalDivisor;
            const pricingSlashUnitAmountString =
              intervalUnitAmount === 1
                ? `${currencyString}/${I18n.t('mapview.spaceInfo.starting')} ${I18n.t('datetime.' + intervalUnit)}`
                : `${currencyString}/${I18n.t('mapview.spaceInfo.starting')} ${intervalUnitAmount} ${I18n.t(
                    'datetime.' + intervalUnit + 's',
                  )}`;

            return (
              <View
                key={chargeRate.min}
                style={{
                  flexDirection: 'row',
                  paddingVertical: 12,
                  paddingHorizontal: 4,
                  borderColor: colors.primary,
                  borderWidth: 1,
                  borderTopWidth: 0,
                }}
              >
                <Typography variant="body1" style={{ flex: 1 }}>
                  {durationString}
                </Typography>
                <Typography variant="body1" style={{ flex: 1 }}>
                  {pricingSlashUnitAmountString}
                </Typography>
              </View>
            );
          })
        : !isLoading && <Typography variant="body1">{I18n.t('mapview.spaceInfo.failedToGetPricing')}</Typography>}
    </Container>
  );
};
